import { useMemo, useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import { each } from 'lodash';
import { req, useApiGet } from 'react-reqq-lite';

import { transformCompareChart, transformCompareTable } from 'helper';

import {
  getTransactions,
  getTransactionGraph,
  getTopSubscribers,
} from './actions';
import * as c from './constant';

export const useTransactions = (params) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-transactions', params],
    queryFn: getTransactions,
    retry: false,
    keepPreviousData: true,
  });

  const data = raw?.data?.data;
  const meta = raw?.data?.meta;

  return [isLoading, data, meta];
};

export const useLineChart = (params) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-transaction-graph', params],
    queryFn: getTransactionGraph,
    retry: false,
    keepPreviousData: true,
  });

  const data = raw?.data?.data;

  const chart = useMemo(
    () =>
      (data || [])?.map((x, i) => ({
        id: i,
        title: x?.transaction_date_time,
        count: x?.transaction_count,
      })),
    [data],
  );

  return [isLoading, chart, ''];
};

export const useTopSubs = (params) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-top-subscribers', params],
    queryFn: getTopSubscribers,
    retry: false,
    keepPreviousData: true,
  });

  const data = raw?.data?.data;

  const chart = useMemo(
    () =>
      (data || [])?.map((x, i) => ({
        id: i,
        title: x?.organization_name,
        count: x?.collections,
        transactions: x?.transactions,
      })),
    [data],
  );

  return [isLoading, chart, ''];
};

export const useCompare = (
  items,
  mode = 'day',
  chartValueKey = 'transaction',
) => {
  const [isLoading] = useState(false);
  const data = useApiGet(c.TRANSACTION_COMPARE, {});
  const dataKeysRef = useRef([]);

  useEffect(() => {
    const ids = {};

    items.forEach((item) => {
      const id = c.buildId(item, mode);
      if (dataKeysRef.current.indexOf(id) < 0) {
        dataKeysRef.current.push(id);
        ids[id] = {
          date_from: dayjs(item).startOf(mode).format('YYYY-MM-DD'),
          date_to: dayjs(item).endOf(mode).format('YYYY-MM-DD'),
          type: c.MAP_GROUP_FULL_NAME[mode],
        };
      }
    });

    each(ids, (params, id) => {
      req.get({
        key: `${c.TRANSACTION_COMPARE}/${id}`,
        url: '/core_wallet/v1/wallet_ledger/graph',
        params,
        onSuccess: (res, state) => {
          req.set(c.TRANSACTION_COMPARE, {
            ...(state[c.TRANSACTION_COMPARE] || {}),
            [id]: {
              table: transformCompareTable(mode)(res?.response),
              chart: transformCompareChart(mode, params)(res?.response),
            },
          });
        },
      });
    });
  }, [mode, items]);

  const chart = useMemo(() => {
    const series = [];
    const ids = {};

    items.forEach((item) => {
      ids[c.buildId(item, mode)] = item;
    });

    each(Object.keys(ids).sort(), (id) => {
      series.push({
        name: dayjs(ids[id]).format(c.MAP_SERIES_FORMAT[mode]),
        data: (data[id]?.chart || []).map((x) => x[chartValueKey]),
      });
    });

    return series;
  }, [data, items, mode, chartValueKey]);

  return [isLoading, data, chart];
};

export default {};
